import clsx from 'clsx';
import { Command } from 'cmdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { CommandOptionType } from 'src/WebApiController';

import * as styles from './CommandMenu.css';
import { useGlobalCommandOptions } from './CommandMenu.hooks';

const SystemActions = [CommandOptionType.ToggleTheme];

export const matchesShortcut = (
  event: KeyboardEvent,
  shortcut?: string
): boolean => {
  if (!shortcut) return false;

  const keys = shortcut.toLowerCase().split('+');
  const lastKey = keys[keys.length - 1];

  if (event.key.toLowerCase() !== lastKey) return false;

  const hasCmd = keys.includes('cmd');
  const hasCtrl = keys.includes('ctrl');
  const hasAlt = keys.includes('alt');
  const hasShift = keys.includes('shift');

  if (hasCmd && !event.metaKey) return false;
  if (hasCtrl && !event.ctrlKey) return false;
  if (hasAlt && !event.altKey) return false;
  if (hasShift && !event.shiftKey) return false;

  const extraModifiers =
    event.metaKey !== hasCmd ||
    event.ctrlKey !== hasCtrl ||
    event.altKey !== hasAlt ||
    event.shiftKey !== hasShift;

  if (extraModifiers) return false;

  return true;
};

const isMac = navigator.userAgent.toLowerCase().includes('mac');

export const formatShortcut = (shortcut?: string): string => {
  if (!shortcut) return '';

  return shortcut
    .split('+')
    .map((part) => {
      if (part === 'cmd') return isMac ? '⌘' : 'Ctrl';
      if (part === 'ctrl') return 'Ctrl';
      if (part === 'alt') return 'Alt';
      if (part === 'shift') return '⇧';
      if (part === 'arrowup') return '↑';
      if (part === 'arrowdown') return '↓';
      if (part === 'arrowright') return '→';
      if (part === 'arrowleft') return '←';
      return part.toUpperCase();
    })
    .join(' + ');
};

export const platformShortcut = (shortcut?: string) => {
  if (!shortcut) return '';

  return isMac ? shortcut : shortcut.replace('cmd', 'ctrl');
};

export const CommandMenu = () => {
  const [search, setSearch] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const { isDarkMode } = useSiteTheme();

  const {
    isCommandMenuOpened: open,
    setIsCommandMenuOpened: setOpen,
    getCommands,
  } = useCommandMenuContext();

  // Fetch global commands and registered commands
  const { globalNavCommands } = useGlobalCommandOptions();
  const registeredCommandsDisplay = getCommands();
  const [registeredActions, systemAction] = registeredCommandsDisplay.reduce(
    ([actions, system], option) => {
      if (SystemActions.includes(option.id as CommandOptionType)) {
        system.push(option);
      } else {
        actions.push(option);
      }
      return [actions, system];
    },
    [[], []] as [
      typeof registeredCommandsDisplay,
      typeof registeredCommandsDisplay,
    ]
  );

  const allCommandOptions = useMemo(() => {
    const platformOptions = [
      ...registeredCommandsDisplay,
      ...globalNavCommands,
    ].map((option) => ({
      ...option,
      shortcut: platformShortcut(option.shortcut ?? ''),
    }));
    return platformOptions;
  }, [registeredCommandsDisplay, globalNavCommands]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setOpen]);

  useEffect(() => {
    const handleKeyDownInCommandMenu = (event: KeyboardEvent) => {
      if (!open) return;

      if (event.key.toLowerCase() === 'escape') {
        event.stopImmediatePropagation();
        event.preventDefault();
        setOpen(false);
        return;
      }

      const allShortcuts = allCommandOptions.map((option) =>
        option.shortcut?.toLowerCase()
      );

      if (
        (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) &&
        allShortcuts.some((shortcut) => matchesShortcut(event, shortcut))
      ) {
        event.stopPropagation();
        event.preventDefault();
      }

      const matchingOption = allCommandOptions.find((option) => {
        return matchesShortcut(event, option.shortcut);
      });

      if (matchingOption) {
        event.preventDefault();
        matchingOption.onSelect();
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDownInCommandMenu, {
        capture: true,
      });
    }

    return () =>
      document.removeEventListener('keydown', handleKeyDownInCommandMenu, {
        capture: true,
      });
  }, [open, allCommandOptions, setOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dialogRef.current &&
        !dialogRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  const placeholderText = useContent(ContentId.CommandMenuPrompt);

  return (
    <Command.Dialog
      ref={dialogRef}
      open={open}
      onOpenChange={setOpen}
      label="Terminal"
      overlayClassName={clsx(styles.overlayStyles, {
        [styles.overlayStylesDark]: isDarkMode,
      })}
      contentClassName={clsx(styles.contentStyles, {
        [styles.contentStyleDark]: isDarkMode,
      })}
      loop
    >
      <div className={styles.inputWrapper}>
        <Command.Input
          className={clsx(styles.inputContainer, {
            [styles.contentStyleDark]: isDarkMode,
          })}
          value={search}
          onValueChange={setSearch}
          placeholder={placeholderText}
        />
        <div className={styles.badgeContainer}>
          <div
            className={clsx(styles.betaBadge, {
              [styles.betaBadgeDark]: isDarkMode,
            })}
          >
            <Content id={ContentId.Beta} />
          </div>
        </div>
      </div>
      <Command.List className={styles.listContainer}>
        <Command.Empty>
          <span className={styles.itemTextContainer}>
            <Content id={ContentId.NoResultFound} />
          </span>
        </Command.Empty>

        <div className={styles.groupsContainer}>
          {registeredActions.length > 0 && (
            <Command.Group
              heading="Actions"
              className={clsx(styles.itemGroup, {
                [styles.darkModetext]: isDarkMode,
              })}
            >
              {registeredActions.map((option, idx) => (
                <Command.Item
                  key={idx}
                  className={clsx(styles.itemStyles, {
                    [styles.itemStyles]: isDarkMode,
                  })}
                  onSelect={(value) => {
                    option.onSelect();
                    setOpen(false);
                  }}
                >
                  <span className={styles.itemTextContainer}>
                    {option.label}
                  </span>
                  {option.shortcut && (
                    <span
                      className={clsx(styles.itemShortcutContainer, {
                        [styles.itemShortcutContainerDark]: isDarkMode,
                      })}
                    >
                      {formatShortcut(option.shortcut)}
                    </span>
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          <Command.Group
            heading="Navigation"
            className={clsx(styles.itemGroup, {
              [styles.darkModetext]: isDarkMode,
            })}
          >
            {globalNavCommands.map((option, idx) => {
              return (
                <Command.Item
                  key={idx}
                  className={styles.itemStyles}
                  onSelect={(value) => {
                    option.onSelect();
                    setOpen(false);
                  }}
                >
                  <span
                    className={clsx(styles.itemTextContainer, {
                      [styles.darkModetext]: isDarkMode,
                    })}
                  >
                    {option.icon ? (
                      <div className={styles.iconLabelContainer}>
                        <div className={styles.icon}>
                          <option.icon
                            withHoverEffect
                            fill={IconsFill.currentColor}
                          />
                        </div>
                        <span>{option.label}</span>
                      </div>
                    ) : (
                      <>{option.label}</>
                    )}
                  </span>
                  {option.shortcut && (
                    <span
                      className={clsx(styles.itemShortcutContainer, {
                        [styles.itemShortcutContainerDark]: isDarkMode,
                      })}
                    >
                      {formatShortcut(option.shortcut)}
                    </span>
                  )}
                </Command.Item>
              );
            })}
          </Command.Group>

          <Command.Group
            heading="System Actions"
            className={clsx(styles.itemGroup, {
              [styles.darkModetext]: isDarkMode,
            })}
          >
            {systemAction.map((option, idx) => (
              <Command.Item
                key={idx}
                className={clsx(styles.itemStyles, {
                  [styles.itemStyles]: isDarkMode,
                })}
                onSelect={(value) => {
                  option.onSelect();
                  setOpen(false);
                }}
              >
                <span className={styles.itemTextContainer}>{option.label}</span>
                {option.shortcut && (
                  <span
                    className={clsx(styles.itemShortcutContainer, {
                      [styles.itemShortcutContainerDark]: isDarkMode,
                    })}
                  >
                    {formatShortcut(option.shortcut)}
                  </span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        </div>
      </Command.List>
    </Command.Dialog>
  );
};
